import { AsyncPipe } from '@angular/common';
import { Component, DestroyRef, OnDestroy, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import { BehaviorSubject, distinctUntilChanged, filter, map, Observable, of, startWith, switchMap } from 'rxjs';
import { DivIcon, Layer, Marker, marker } from 'leaflet';
import { DeviceActions } from '../../../devices/state/device.actions';
import { selectAllDevices, selectDeviceById, selectIsDevicesLoading } from '../../../devices/state/device.selectors';
import { MapComponent } from '../../components/map/map.component';
import { MapDevice } from '../../models/map-device.interface';
import { getMapIcon } from '../../utils/map-utils';
import { CardComponent } from "../../../../shared/components/card/card.component";
import { MapDeviceDetailComponent } from '../../components/map-device-detail/map-device-detail.component';

@Component({
  selector: 'app-page-map',
  imports: [
    AsyncPipe,
    TranslatePipe,
    MapComponent,
    CardComponent,
    MapDeviceDetailComponent
],
  templateUrl: './page-map.component.html',
  styleUrl: './page-map.component.scss'
})
export class PageMapComponent implements OnInit, OnDestroy {
  private destroyRef = inject(DestroyRef);
  private store = inject(Store);
  private selectedDeviceId$ = new BehaviorSubject<string | null>(null);
  private deviceMarkers = new Map<string, Marker>();
  private devicesById = new Map<string, MapDevice>();

  loading$ = this.store.select(selectIsDevicesLoading);
  selectedDevice$ = this.selectedDeviceId$.pipe(
    distinctUntilChanged(),
    switchMap(id => id ? this.store.select(selectDeviceById(id)) : of(null)),
  );

  layers$: Observable<Layer[]> = this.store.select(selectAllDevices).pipe(
    map(devices =>
      devices.map(d => ({
        id: d.id,
        name: d.name,
        latitude: d.location?.lat,
        longitude: d.location?.lon,
        type: d.type,
        isFavorite: d.isFavorite,
        isOutOfService: d.isOutOfService,
      } as MapDevice))
    ),
    distinctUntilChanged((prev, curr) => this.devicesChanged(prev, curr)),
    map(devices => {
      this.syncDeviceCaches(devices);
      return devices
        .filter((device): device is MapDevice & { latitude: number; longitude: number } =>
          !!device.latitude && !!device.longitude
        )
        .map(device => {
          const deviceMarker = this.getOrCreateMarker(device);
          this.deviceMarkers.set(device.id, deviceMarker);
          return deviceMarker;
        });
    }),
    startWith([])
  );

  ngOnInit() {
    this.store.dispatch(DeviceActions.public.loadAllDevicesByFilters({
      filter: {}
    }));

    this.selectedDeviceId$.pipe(
      filter((id): id is string => !!id),
      distinctUntilChanged(),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(id => {
      this.store.dispatch(DeviceActions.public.loadDeviceById({id}));
    });
  }

  ngOnDestroy() {
    for (const marker of this.deviceMarkers.values()) {
      marker.remove();
    }

    this.deviceMarkers.clear();
    this.devicesById.clear();
  }

  onDeviceMarkerClick(device: MapDevice): void {
    this.updateMarkerSelection(device.id);
    this.selectedDeviceId$.next(device.id);
  }

  onCloseDetail(): void {
    this.updateMarkerSelection(null);
    this.selectedDeviceId$.next(null);
  }

  onTriggerDeviceAction(evtData: { id: string, value: boolean }) {
    this.store.dispatch(DeviceActions.public.triggerDeviceAction({
      id: evtData.id,
      value: evtData.value,
    }));
  };

  devicesChanged(a: MapDevice[], b: MapDevice[]): boolean {
    if (a.length !== b.length) return false;

    for (let i = 0; i < a.length; i++) {
      const da = a[i];
      const db = b[i];

      if (
        da.id !== db.id ||
        da.name !== db.name ||
        da.latitude !== db.latitude ||
        da.longitude !== db.longitude ||
        da.type !== db.type ||
        da.isFavorite !== db.isFavorite ||
        da.isOutOfService !== db.isOutOfService
      ) {
        return false;
      }
    }

    return true;
  }

  private getMarkerStatus(device: MapDevice): 'default' | 'favorite' | 'outOfService' {
    if (device.isOutOfService) {
      return 'outOfService';
    }

    if (device.isFavorite) {
      return 'favorite';
    }

    return 'default';
  }

  private getOrCreateMarker(device: MapDevice & { latitude: number; longitude: number }): Marker {
    const existingMarker = this.deviceMarkers.get(device.id);
    const icon = this.getMarkerIcon(device, this.selectedDeviceId$.value === device.id);

    if (existingMarker) {
      existingMarker.setLatLng([device.latitude, device.longitude]);
      existingMarker.setIcon(icon);
      existingMarker.setTooltipContent(device.name);
      return existingMarker;
    }

    const deviceMarker = marker([device.latitude, device.longitude], { icon });
    deviceMarker.bindTooltip(device.name);
    deviceMarker.on('click', () => {
      this.onDeviceMarkerClick(device);
    });

    return deviceMarker;
  }

  private getMarkerIcon(device: MapDevice, isSelected: boolean): DivIcon {
    return getMapIcon(device.type, this.getMarkerStatus(device), isSelected);
  }

  private updateMarkerSelection(nextSelectedId: string | null): void {
    const previousSelectedId = this.selectedDeviceId$.value;

    if (previousSelectedId && previousSelectedId !== nextSelectedId) {
      const previousDevice = this.devicesById.get(previousSelectedId) ?? null;
      if (previousDevice) {
        this.deviceMarkers.get(previousSelectedId)?.setIcon(this.getMarkerIcon(previousDevice, false));
      }
    }

    if (!nextSelectedId) {
      return;
    }

    const currentDevice = this.devicesById.get(nextSelectedId) ?? null;
    if (currentDevice) {
      this.deviceMarkers.get(nextSelectedId)?.setIcon(this.getMarkerIcon(currentDevice, true));
    }
  }

  private syncDeviceCaches(devices: MapDevice[]) {
    const nextIds = new Set(devices.map(device => device.id));

    for (const id of this.devicesById.keys()) {
      if (!nextIds.has(id)) {
        this.devicesById.delete(id);
        const marker = this.deviceMarkers.get(id);
        marker?.remove();
        this.deviceMarkers.delete(id);
      }
    }

    for (const device of devices) {
      this.devicesById.set(device.id, device);
    }

    const selectedId = this.selectedDeviceId$.value;
    if (selectedId && !nextIds.has(selectedId)) {
      this.selectedDeviceId$.next(null);
    }
  }
}
